/**
 * StreamViewer Component
 * For supervisors to view RBT live streams
 */

import React, { useRef, useState, useEffect } from 'react';
import { webRTCService, StreamSession } from '../services/WebRTCService';
import { Video, VideoOff, Wifi, Maximize2, Volume2, VolumeX, User, X } from 'lucide-react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

interface StreamViewerProps {
  supervisorId: string;
  sessionNoteId?: string; // Optional: watch specific session
  onStreamEnd?: () => void;
}

export const StreamViewer: React.FC<StreamViewerProps> = ({
  supervisorId,
  sessionNoteId,
  onStreamEnd,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [availableStreams, setAvailableStreams] = useState<StreamSession[]>([]);
  const [selectedStream, setSelectedStream] = useState<StreamSession | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [muted, setMuted] = useState(false);
  const [, setFullscreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewDuration, setViewDuration] = useState(0);

  // Listen for available streams
  useEffect(() => {
    const q = query(
      collection(db, 'streamSessions'),
      where('supervisorId', '==', supervisorId),
      where('status', 'in', ['waiting', 'live'])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const streams: StreamSession[] = [];
      snapshot.forEach(doc => {
        streams.push({ id: doc.id, ...doc.data() } as StreamSession);
      });
      setAvailableStreams(streams);

      // Auto-select if only one stream and not connected
      if (streams.length === 1 && !connected && !connecting) {
        setSelectedStream(streams[0]);
      }

      // Auto-select specific session if provided
      if (sessionNoteId && !connected) {
        const targetStream = streams.find(s => s.sessionNoteId === sessionNoteId);
        if (targetStream) {
          setSelectedStream(targetStream);
        }
      }
    });

    return () => unsubscribe();
  }, [supervisorId, sessionNoteId, connected, connecting]);

  // View duration timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (connected) {
      interval = setInterval(() => {
        setViewDuration(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [connected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      webRTCService.cleanup();
    };
  }, []);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleConnect = async (stream: StreamSession) => {
    if (!videoRef.current) return;

    setConnecting(true);
    setError(null);
    setSelectedStream(stream);

    try {
      await webRTCService.joinStreamSession(stream.id, videoRef.current);
      setConnected(true);
      setViewDuration(0);
    } catch (err: any) {
      setError(err.message || 'Failed to connect to stream');
      setConnected(false);
    } finally {
      setConnecting(false);
    }
  };

  // Leave the stream (viewer-only) — the RBT keeps broadcasting and the stream
  // stays listed so the supervisor can rejoin. To actually stop the RBT's
  // stream, use the ✕ (End) control on the stream list.
  const handleDisconnect = () => {
    webRTCService.leaveStream();
    setConnected(false);
    setSelectedStream(null);
    onStreamEnd?.();
  };

  // Actually stop the RBT's stream (marks the doc ended for everyone).
  const handleEndStream = async () => {
    if (selectedStream) {
      await webRTCService.endStreamById(selectedStream.id).catch(() => {});
    }
    webRTCService.leaveStream();
    setConnected(false);
    setSelectedStream(null);
    onStreamEnd?.();
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted(!muted);
    }
  };

  const toggleFullscreen = () => {
    if (!videoRef.current) return;

    if (!document.fullscreenElement) {
      videoRef.current.requestFullscreen?.();
      setFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setFullscreen(false);
    }
  };

  return (
    <div style={{
      backgroundColor: '#000',
      borderRadius: '12px',
      overflow: 'hidden',
    }}>
      {/* Video Display */}
      <div style={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%', // 16:9 aspect ratio
        backgroundColor: '#1f2937',
      }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />

        {/* No stream overlay */}
        {!connected && !connecting && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
          }}>
            <VideoOff size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p style={{ opacity: 0.7, marginBottom: '16px' }}>
              {availableStreams.length === 0
                ? 'No active streams'
                : 'Select a stream to watch'}
            </p>
          </div>
        )}

        {/* Connecting overlay */}
        {connecting && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid rgba(255, 255, 255, 0.2)',
              borderTop: '4px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '16px',
            }} />
            <p style={{ opacity: 0.7 }}>Connecting to stream...</p>
          </div>
        )}

        {/* Live indicator */}
        {connected && (
          <div style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '6px 12px',
            borderRadius: '20px',
          }}>
            <Wifi size={16} color="#16a34a" />
            <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>VIEWING</span>
            <span style={{ color: '#9ca3af', fontSize: '12px' }}>
              {formatDuration(viewDuration)}
            </span>
          </div>
        )}

        {/* RBT info */}
        {connected && selectedStream && (
          <div style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '6px 12px',
            borderRadius: '20px',
          }}>
            <User size={16} color="white" />
            <span style={{ color: 'white', fontSize: '12px' }}>
              {selectedStream.rbtName}
            </span>
          </div>
        )}

        {/* Video controls overlay */}
        {connected && (
          <div style={{
            position: 'absolute',
            bottom: '12px',
            right: '12px',
            display: 'flex',
            gap: '8px',
          }}>
            <button
              onClick={toggleMute}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '8px',
                borderRadius: '50%',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
            <button
              onClick={toggleFullscreen}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '8px',
                borderRadius: '50%',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Maximize2 size={20} />
            </button>
          </div>
        )}
      </div>

      {/* Controls / Stream List */}
      <div style={{
        padding: '12px',
        backgroundColor: '#2d2d2d',
      }}>
        {/* Connected — Leave keeps the RBT streaming (rejoinable); End stops it. */}
        {connected && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={handleDisconnect}
              style={{
                backgroundColor: '#374151',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '24px',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              <VideoOff size={20} />
              Leave (keep streaming)
            </button>
            <button
              onClick={handleEndStream}
              style={{
                backgroundColor: '#dc2626',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '24px',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              <X size={20} />
              End stream
            </button>
          </div>
        )}

        {/* Not connected - show available streams */}
        {!connected && !connecting && availableStreams.length > 0 && (
          <div>
            <p style={{
              color: '#9ca3af',
              fontSize: '12px',
              marginBottom: '12px',
              textAlign: 'center',
            }}>
              Available Streams ({availableStreams.length})
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {availableStreams.map(stream => (
                <div key={stream.id} style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                  <button
                    onClick={() => handleConnect(stream)}
                    style={{
                      backgroundColor: '#374151',
                      color: 'white',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flex: 1,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: stream.status === 'live' ? '#16a34a' : '#eab308',
                      }} />
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: 'bold' }}>{stream.rbtName}</div>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                          {stream.status === 'live' ? 'Live now' : 'Waiting...'}
                        </div>
                      </div>
                    </div>
                    <Video size={20} />
                  </button>
                  {/* Dismiss a stale/orphaned stream (e.g. the RBT closed the app
                      without stopping). Marks it ended so it drops off the list. */}
                  <button
                    onClick={() => webRTCService.endStreamById(stream.id).catch(() => {})}
                    title="End / dismiss this stream"
                    aria-label={`End stream for ${stream.rbtName}`}
                    style={{
                      backgroundColor: 'transparent',
                      color: '#9ca3af',
                      padding: '0 12px',
                      borderRadius: '8px',
                      border: '1px solid #374151',
                      cursor: 'pointer',
                    }}
                  >
                    <X size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No streams available */}
        {!connected && !connecting && availableStreams.length === 0 && (
          <p style={{
            color: '#6b6b6b',
            fontSize: '14px',
            textAlign: 'center',
            padding: '8px',
          }}>
            No RBTs are currently streaming
          </p>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          borderTop: '1px solid #fecaca',
          padding: '12px',
          color: '#dc2626',
          fontSize: '14px',
          textAlign: 'center',
        }}>
          {error}
        </div>
      )}

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default StreamViewer;
